import React from 'react'
import styled, { css } from 'styled-components'
import { object, bool } from 'prop-types'

import { pricify } from 'utils/pricify'

import * as spacing from 'styles/spacing'
import { grey, white, black, borderColor, gold } from 'styles/colors'
import { borderRadius, square } from 'styles/global'
import { fontWeights, fontSizes } from 'styles/typography'

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  color: ${black};
  font-size: ${fontSizes.tiny};

  &:first-child {
    margin-top: ${spacing.small};
  }

  & + & {
    padding-top: ${spacing.medium};
  }
`

export const PhotoWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-right: ${spacing.medium};
  ${square('40px')};
`

export const Photo = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: ${borderRadius};
  border: 1px solid ${borderColor};
`

export const Quantity = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  line-height: 14px;
  text-align: center;
  background-color: ${props => (props.isSubscription ? gold : grey)};
  color: ${white};
  font-weight: ${fontWeights.bold};
  font-size: 10px;
  border-radius: 50%;
  letter-spacing: -0.5px;
  transform: translate3d(25%, -25%, 0);
  ${square`14px`};
`

export const Title = styled.span`
  flex: 1;
  padding-right: ${spacing.medium};
`

const PriceWrap = styled.div`
  text-align: right;
`

const Price = styled.strong`
  color: ${black};
  text-align: right;

  ${props =>
    props.linethrough &&
    css`
      font-weight: ${fontWeights.regular};
      text-decoration: line-through;
      display: block;
      color: ${grey};
      font-size: ${fontSizes.tiny};
    `}
`

export const Variant = styled.span`
  display: block;
  font-size: ${fontSizes.mini};
  margin-top: 2px;
  color: ${grey};
`

const Product = ({ product, isSubscription }) => {
  const renderPrice = () => {
    const initialPrice = parseFloat(product.variant.price) * parseFloat(product.quantity)
    const discount = product.discountAllocations.find(
      discountAllocation => discountAllocation.allocatedAmount.amount === initialPrice.toString()
    )
    let priceAfterDiscount = discount ? initialPrice - parseFloat(discount.allocatedAmount.amount) : initialPrice

    if (discount)
      return (
        <PriceWrap>
          <Price linethrough>{pricify({ amount: initialPrice })}</Price>
          <Price>
            <span>{pricify({ amount: priceAfterDiscount })}</span>
            {isSubscription && (
              <span>
                /<br />
                month
              </span>
            )}
          </Price>
        </PriceWrap>
      )

    return (
      <Price>
        <span>{pricify({ amount: parseFloat(product.variant.price) * parseFloat(product.quantity) })}</span>
        {isSubscription && (
          <span>
            /<br />
            month
          </span>
        )}
      </Price>
    )
  }

  return (
    <Container>
      <PhotoWrap>
        <Photo src={product.variant.image?.src} alt={product.title} />
        <Quantity isSubscription={isSubscription}>{product.quantity}</Quantity>
      </PhotoWrap>
      <Title>
        <span>{product.title}</span>
        {product.variant && product.variant.title !== 'Default Title' && <Variant>{product.variant.title}</Variant>}
      </Title>
      {renderPrice()}
    </Container>
  )
}

Product.propTypes = {
  product: object,
  isSubscription: bool
}

export default Product
